// Theo Memory System — production setup SQL, split into 3 verifiable steps.
//
// Each step is plain SQL with NO dollar-quoted blocks, NO information_schema
// joins, NO loops. Every statement is idempotent and self-contained.
//
// Use case for the staged approach:
//   1. STEP_1_MIN  — creates ONLY theo_conversations. Smallest possible
//                    migration. If this fails, the problem is environmental.
//   2. STEP_2_TABLES — adds the remaining 3 tables (memories, goals, checkins).
//   3. STEP_3_RLS  — enables row-level security + 16 explicit policies.
//
// The "Copy setup SQL" button copies STEP_1_MIN + STEP_2_TABLES + STEP_3_RLS
// concatenated. If a user reports a failure, we now also expose a "Copy
// minimal (1 table)" button so we can isolate the problem.

export const STEP_1_MIN = `CREATE TABLE IF NOT EXISTS public.theo_conversations (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  title text DEFAULT 'Conversation with Theo',
  messages jsonb NOT NULL DEFAULT '[]'::jsonb,
  created_at timestamptz NOT NULL DEFAULT now(),
  updated_at timestamptz NOT NULL DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_theo_conversations_user
  ON public.theo_conversations (user_id, updated_at DESC);
`;

export const STEP_2_TABLES = `CREATE TABLE IF NOT EXISTS public.theo_memories (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  memory_text text NOT NULL,
  created_at timestamptz NOT NULL DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_theo_memories_user
  ON public.theo_memories (user_id, created_at DESC);

CREATE UNIQUE INDEX IF NOT EXISTS uq_theo_memories_user_text_ci
  ON public.theo_memories (user_id, LOWER(memory_text));

CREATE TABLE IF NOT EXISTS public.theo_goals (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  goal_text text NOT NULL,
  active boolean NOT NULL DEFAULT true,
  created_at timestamptz NOT NULL DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_theo_goals_user
  ON public.theo_goals (user_id, active);

CREATE TABLE IF NOT EXISTS public.theo_checkins (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id uuid NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  checkin_date date NOT NULL DEFAULT CURRENT_DATE,
  prompt text,
  response text,
  created_at timestamptz NOT NULL DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_theo_checkins_user
  ON public.theo_checkins (user_id, checkin_date DESC);
`;

// Explicit RLS — no DO blocks, no loops, no dollar quoting.
// 16 policies (4 tables x 4 verbs). Safe to run multiple times because of
// "DROP POLICY IF EXISTS" before each create.
export const STEP_3_RLS = `ALTER TABLE public.theo_conversations ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.theo_memories ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.theo_goals ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.theo_checkins ENABLE ROW LEVEL SECURITY;

DROP POLICY IF EXISTS sel ON public.theo_conversations;
DROP POLICY IF EXISTS ins ON public.theo_conversations;
DROP POLICY IF EXISTS upd ON public.theo_conversations;
DROP POLICY IF EXISTS del ON public.theo_conversations;
CREATE POLICY sel ON public.theo_conversations FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY ins ON public.theo_conversations FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY upd ON public.theo_conversations FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY del ON public.theo_conversations FOR DELETE TO authenticated USING (auth.uid() = user_id);

DROP POLICY IF EXISTS sel ON public.theo_memories;
DROP POLICY IF EXISTS ins ON public.theo_memories;
DROP POLICY IF EXISTS upd ON public.theo_memories;
DROP POLICY IF EXISTS del ON public.theo_memories;
CREATE POLICY sel ON public.theo_memories FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY ins ON public.theo_memories FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY upd ON public.theo_memories FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY del ON public.theo_memories FOR DELETE TO authenticated USING (auth.uid() = user_id);

DROP POLICY IF EXISTS sel ON public.theo_goals;
DROP POLICY IF EXISTS ins ON public.theo_goals;
DROP POLICY IF EXISTS upd ON public.theo_goals;
DROP POLICY IF EXISTS del ON public.theo_goals;
CREATE POLICY sel ON public.theo_goals FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY ins ON public.theo_goals FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY upd ON public.theo_goals FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY del ON public.theo_goals FOR DELETE TO authenticated USING (auth.uid() = user_id);

DROP POLICY IF EXISTS sel ON public.theo_checkins;
DROP POLICY IF EXISTS ins ON public.theo_checkins;
DROP POLICY IF EXISTS upd ON public.theo_checkins;
DROP POLICY IF EXISTS del ON public.theo_checkins;
CREATE POLICY sel ON public.theo_checkins FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY ins ON public.theo_checkins FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY upd ON public.theo_checkins FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY del ON public.theo_checkins FOR DELETE TO authenticated USING (auth.uid() = user_id);
`;

// Full setup = step1 + step2 + step3, concatenated.
export const THEO_SETUP_SQL = STEP_1_MIN + "\n" + STEP_2_TABLES + "\n" + STEP_3_RLS;

// Phase 4A migration — archive flag + notification prefs + scheduled pushes.
// Idempotent; safe to run on a project that already has the Theo tables.
export const PHASE_4A_SQL = `-- Phase 4A: Archive + Notification infrastructure
ALTER TABLE public.theo_conversations
  ADD COLUMN IF NOT EXISTS archived boolean NOT NULL DEFAULT false;

CREATE INDEX IF NOT EXISTS idx_theo_conversations_user_archived
  ON public.theo_conversations (user_id, archived, updated_at DESC);

CREATE TABLE IF NOT EXISTS public.theo_notification_prefs (
  user_id          uuid PRIMARY KEY REFERENCES auth.users(id) ON DELETE CASCADE,
  push_enabled     boolean NOT NULL DEFAULT false,
  daily_brief      boolean NOT NULL DEFAULT true,
  weekly_recap     boolean NOT NULL DEFAULT true,
  send_hour_local  smallint NOT NULL DEFAULT 8 CHECK (send_hour_local BETWEEN 0 AND 23),
  timezone         text NOT NULL DEFAULT 'UTC',
  last_sent_at     timestamptz,
  next_send_at     timestamptz,
  updated_at       timestamptz NOT NULL DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_theo_prefs_next_send
  ON public.theo_notification_prefs (next_send_at)
  WHERE push_enabled = true;

ALTER TABLE public.theo_notification_prefs ENABLE ROW LEVEL SECURITY;

DROP POLICY IF EXISTS sel ON public.theo_notification_prefs;
DROP POLICY IF EXISTS ins ON public.theo_notification_prefs;
DROP POLICY IF EXISTS upd ON public.theo_notification_prefs;
DROP POLICY IF EXISTS del ON public.theo_notification_prefs;
CREATE POLICY sel ON public.theo_notification_prefs FOR SELECT TO authenticated USING (auth.uid() = user_id);
CREATE POLICY ins ON public.theo_notification_prefs FOR INSERT TO authenticated WITH CHECK (auth.uid() = user_id);
CREATE POLICY upd ON public.theo_notification_prefs FOR UPDATE TO authenticated USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
CREATE POLICY del ON public.theo_notification_prefs FOR DELETE TO authenticated USING (auth.uid() = user_id);

CREATE TABLE IF NOT EXISTS public.theo_scheduled_pushes (
  id            uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id       uuid NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  kind          text NOT NULL CHECK (kind IN ('daily_brief','weekly_recap','custom')),
  scheduled_at  timestamptz NOT NULL,
  sent_at       timestamptz,
  status        text NOT NULL DEFAULT 'pending' CHECK (status IN ('pending','sent','failed','skipped')),
  payload       jsonb,
  error         text,
  created_at    timestamptz NOT NULL DEFAULT now()
);

CREATE INDEX IF NOT EXISTS idx_theo_pushes_due
  ON public.theo_scheduled_pushes (scheduled_at)
  WHERE status = 'pending';
CREATE INDEX IF NOT EXISTS idx_theo_pushes_user
  ON public.theo_scheduled_pushes (user_id, scheduled_at DESC);

ALTER TABLE public.theo_scheduled_pushes ENABLE ROW LEVEL SECURITY;

DROP POLICY IF EXISTS sel ON public.theo_scheduled_pushes;
CREATE POLICY sel ON public.theo_scheduled_pushes FOR SELECT TO authenticated USING (auth.uid() = user_id);
`;
